import { StackScreenProps } from '@react-navigation/stack'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import Toast from 'react-native-root-toast'
import Row from '../../../@ui/containers/grid/row'
import { Copyright } from '../../../@ui/typo/copyright.typo'
import { ReplayLevelIndicator } from '../clusters/shared/LevelIndicator'
import { useGlobalStyles } from '../../../contexts/global-styles.provider'
import { ReplayNavigatorParams } from '../../../navigation/types'
import { TargetType } from '../../../generated/graphql'

import * as Sentry from '@sentry/react-native'
import { MelddSearchBar } from '../../../@ui/form/meldd-search.input'
import { IconFilter } from '../../../utils/meldd-icons'
import { ClusterListQuery } from '../../../generated/graphql'
import { NoDataMessage } from '../../../@ui/containers/lists/no-data'
import ClusterList from '../clusters/cluster-list/components/cluster.list'
import { useReplayApi } from '../../../services/snapshots/replay-api.hook'
import { useTheme } from '../../../hooks/theme.hook'

export function ReplayClusterListScreen({
  navigation,
}: StackScreenProps<ReplayNavigatorParams, 'ReplayClusterList'>) {
  const { t } = useTranslation('clusters')
  const globalStyles = useGlobalStyles()
  const theme = useTheme()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [data, setData] = useState<ClusterListQuery['availableClusters']>([])
  const { clusterService, tick, nodeId } = useReplayApi()

  const refresh = useCallback(
    async ({ searchQuery = '' }: { searchQuery?: string }) => {
      try {
        const clusters = await clusterService.getClusterList(searchQuery)
        setData(clusters)
      } catch (e) {
        console.error(e)
        Toast.show((e as Error).message)
        Sentry.captureException(e)
      }
    },
    [clusterService]
  )

  useEffect(() => {
    refresh({ searchQuery })
  }, [tick, searchQuery, refresh])

  useEffect(() => {
    setSearchInput('')
    setSearchQuery('')
  }, [])

  return (
    <View style={{ ...globalStyles.pageContainer, padding: theme.spacing(2) }}>
      <View>
        <Row alignItems={'center'} columnGap={2} flex={1}>
          <View style={{ flex: 1, height: 40 }}>
            <MelddSearchBar
              icon={IconFilter}
              placeholder={t('search-clusters')}
              onChangeText={setSearchInput}
              onSearch={async (searchQuery) => {
                setSearchQuery(searchQuery)
              }}
              value={searchInput}
            />
          </View>
          <View>
            <ReplayLevelIndicator
              target={TargetType.Node}
              targetId={nodeId}
              flat={true}
            />
          </View>
        </Row>
      </View>
      <ClusterList
        data={data}
        loading={false}
        onRefresh={() => refresh({})}
        onClusterSelected={(clusterId) => {
          navigation.navigate('ReplayClusterCards', {
            clusterId,
          })
        }}
        onClusterContributionsPress={(clusterId) => {
          navigation.navigate('ReplayClusterContributions', {
            clusterId,
          })
        }}
        NoData={
          <NoDataMessage
            labels={{
              noDataMessage: t('no-clusters-found'),
            }}
          />
        }
      />
      <Copyright />
    </View>
  )
}
export default ReplayClusterListScreen
